/**
 * Customer-facing copy.
 *
 * A payment page is where trust is either kept or lost, and a customer who
 * cannot read the page cannot give informed consent to a charge. The
 * languages here are the ones WyoCSPay merchants actually sell into.
 *
 * English is the contract: `Strings` is derived from it, so a missing key in
 * any other language is a compile error rather than an untranslated word
 * appearing next to an amount of money.
 */
export const STRINGS = {
  en: {
    secure: 'Secure connection',
    merchant: 'Merchant',
    reference: 'Reference',
    description: 'Description',
    amount: 'Amount',
    total: 'Total due',
    summary: 'Order summary',
    payNow: 'Pay',
    paying: 'Submitting…',
    chooseMethod: 'Choose how to pay',
    methodsNote: 'Available methods are determined by your card issuer, your country and the merchant’s account.',
    unavailableTitle: 'Not offered here',
    confirmingHead: 'Confirming your payment',
    confirmingBody: 'We are waiting for confirmation from the payment network. Please keep this window open - do not refresh or close it.',
    confirmingWhy: 'Your bank has been contacted. We only mark a payment as complete once the payment network confirms it directly to our servers, not when your browser returns to this page.',
    okHead: 'Payment confirmed',
    okBody: 'has been paid.',
    failHead: 'Payment declined',
    failBody: 'Your bank did not authorise this charge. No money has left your account.',
    failHint: 'If this keeps happening, contact your card issuer or try a different payment method.',
    tryAgain: 'Try again',
    amountCharged: 'Amount charged',
    amountAttempted: 'Amount attempted',
    paidAt: 'Confirmed at',
    method: 'Payment method',
    expiredHead: 'This payment link has expired',
    expiredBody: 'Ask the merchant to send you a new one.',
    revokedHead: 'This payment link was cancelled',
    revokedBody: 'Contact the merchant if you believe this is a mistake.',
    notFoundHead: 'This payment link is not valid',
    notFoundBody: 'Check the link you were given, or ask the merchant to resend it.',
    unavailableHead: 'Payments are not available right now',
    unavailableBody: 'The payment processor is not configured for this platform. No payment can be taken, and nothing has been charged.',
    errorHead: 'Something went wrong',
    loading: 'Loading payment…',
    language: 'Language',
    poweredBy: 'Payments processed by Stripe. Card details are entered directly into Stripe and never reach WyoCSPay servers.',
    terms: 'Terms of Service',
    privacy: 'Privacy Notice',
    support: 'Support',
    acceptPre: 'I have read and accept the',
    acceptMid: 'and the',
    acceptPost: '.',
    acceptRequired: 'Please accept the terms before paying.',
    sellerNote: 'You are paying the merchant named above. WyoCSPay processes the payment; it is not the seller.',
    operatedBy: 'Payment page operated by',
    rights: 'All rights reserved.',
    receiptNote: 'Keep the payment reference above if you need to contact support about this charge.',
    advertisement: 'Advertisement',
    adNote: 'Paid advertising. It has nothing to do with your payment, and opens in a new window.',
    /**
     * Shown to somebody who arrived at this address without a payment link.
     *
     * They have not made a mistake, so nothing here says they have, and nothing
     * here is styled as a warning. It says what the address is and what to do
     * next, and it says nothing about the platform beyond that - no merchant,
     * no identifier, no hint about whether any particular link exists.
     */
    landingHead: 'Secure payment page',
    landingBody: 'This is where WyoCSPay payments are made. There is nothing to enter here: a payment opens from the link the merchant sends you.',
    landingAsk: 'If you are expecting to pay someone, ask them to send you the payment link again.',
    /**
     * The security note in the footer, and the panel it opens.
     *
     * Every sentence below is a statement of fact that somebody can be held to,
     * so the register is the same as the rest of this file: plain, specific,
     * and free of the words a trust seal usually leans on. Nothing here says
     * bank-grade, military-grade, continuous or real-time. The scanning is
     * quarterly and the copy says every three months, because that is what
     * happens and because a customer who later discovers the difference has
     * been told something untrue on a page where they typed a card number.
     *
     * `assuranceCards` deliberately does not repeat `poweredBy` word for word.
     * The footer already states that card details are entered directly into
     * Stripe; this says the same thing from the customer's side, which is what
     * somebody who opened a panel headed "security" came to read.
     */
    assuranceLabel: 'Security and compliance',
    assuranceCardsHead: 'Card details',
    assuranceCards: 'The card form on this page is served by Stripe. What you type into it goes to Stripe, not to us: WyoCSPay never receives or stores your card number.',
    assuranceStandardHead: 'Assessment',
    assuranceStandardNote: 'The standard this payment page is assessed against. SAQ A is the level for a page that leaves all card handling to a validated provider.',
    assuranceScanHead: 'External scanning',
    assuranceScan: 'The systems that serve this page are scanned for vulnerabilities every three months by a scanning vendor approved by the PCI Security Standards Council.',
    assuranceScanLast: 'Most recent scan passed',
    assuranceTlsHead: 'Connection',
    assuranceTls: 'This page is served over TLS 1.2 or 1.3, using authenticated encryption only.',
    assuranceClose: 'Close',
    // Wizard, panels and session band.
    stepMethod: 'Payment method',
    stepConfirm: 'Confirmation',
    stepResult: 'Result',
    txnLabel: 'Transaction no.',
    sessionLabel: 'Session expires in',
    sessionExpired: 'Session expired',
    sessionExpiredNote: 'This payment session has expired and this link can no longer be used. Please request a new payment link.',
    payRequest: 'Payment request',
    invoiceNo: 'Invoice number',
    orderSummary: 'Order summary',
    sumSubtotal: 'Subtotal',
    totalDue: 'Total due',
    continueBtn: 'Continue',
    changeMethod: 'Change method',
    reviewLine: 'Please review and confirm your payment.',
    holdCard: 'The amount is authorized right away; the processing fee is captured once your bank approves the charge.',
    // Result screen.
    resultBand: 'Payment to merchant',
    payInfo: 'Payment information',
    statusLabel: 'Status',
    chargedLabel: 'Amount charged',
    chargedLabelNo: 'Amount attempted',
    pendingHead: 'Processing your payment',
    pendingSub: 'Please keep this window open - do not refresh or close it.',
    pendingKeep: 'Please keep this window open - do not refresh or close it.',
    stepSubmitted: 'Payment submitted',
    stepAuthorized: 'Authorized by your bank',
    stepConfirmed: 'Confirmed by WyoCSPay',
    okHint: 'A receipt has been emailed to you. You can safely return to the merchant.',
    chooseAnother: 'Choose another method',
    securedTitle: 'Secured by WyoCSPay',
    securedBody: 'Processed over an encrypted, PCI DSS Level 1 connection. Your card details were entered directly with the bank and never touched this page.',
    supportQ: 'Questions about this charge? Reach support at',
    // Status timeout.
    timeoutHead: 'Still confirming your payment',
    timeoutSub: 'This is taking longer than usual. Your payment is not lost - you can check again below.',
    timeoutHint: 'We have not had a confirmation from your bank yet. This can take a few minutes. Check again now, or you will get an email the moment it settles - no need to pay again.',
    checkAgain: 'Check again',
    // Trust badges.
    trust3dsTitle: '3-D Secure 2.2',
    trust3dsBody: 'Every charge is authenticated directly with your issuing bank.',
    trustPciTitle: 'PCI DSS Level 1',
    trustPciBody: 'Card data is tokenized and never stored on our servers.',
    trust247Title: 'Around-the-clock support',
    trust247Body: 'Our team answers every transaction inquiry within two hours.',
    // Subscription package payment (/s/).
    subPackageTitle: 'Your WyoCSPay package',
    account: 'Account',
    packageLbl: 'Package',
    billingLbl: 'Billing',
    billingRenewal: 'Monthly renewal',
    billingSetup: 'First month (setup)',
    subAuthNote: 'By paying you authorize monthly charges for this package until you cancel it.',
    subPaidTo: 'Paid to Wyoming Cloud Solutions LLC through Stripe.',
  },
  bg: {
    secure: 'Защитена връзка',
    merchant: 'Търговец',
    reference: 'Референция',
    description: 'Описание',
    amount: 'Сума',
    total: 'Общо за плащане',
    summary: 'Обобщение',
    payNow: 'Плати',
    paying: 'Изпращане…',
    chooseMethod: 'Изберете начин на плащане',
    methodsNote: 'Наличните начини зависят от банката Ви, държавата Ви и профила на търговеца.',
    unavailableTitle: 'Не се предлага тук',
    confirmingHead: 'Потвърждаваме плащането Ви',
    confirmingBody: 'Изчакваме потвърждение от платежната мрежа. Моля, не затваряйте и не презареждайте страницата.',
    confirmingWhy: 'Банката Ви е уведомена. Отбелязваме плащане като успешно едва когато платежната мрежа го потвърди директно на нашите сървъри - не когато браузърът Ви се върне тук.',
    okHead: 'Плащането е потвърдено',
    okBody: 'получи плащането.',
    failHead: 'Плащането е отказано',
    failBody: 'Банката Ви не оторизира транзакцията. Няма удържани средства.',
    failHint: 'Ако се повтори, свържете се с банката си или изберете друг начин на плащане.',
    tryAgain: 'Опитайте отново',
    amountCharged: 'Удържана сума',
    amountAttempted: 'Заявена сума',
    paidAt: 'Потвърдено на',
    method: 'Начин на плащане',
    expiredHead: 'Този линк за плащане е изтекъл',
    expiredBody: 'Помолете търговеца да Ви изпрати нов.',
    revokedHead: 'Този линк за плащане е анулиран',
    revokedBody: 'Свържете се с търговеца, ако смятате, че е грешка.',
    notFoundHead: 'Линкът за плащане не е валиден',
    notFoundBody: 'Проверете линка или помолете търговеца да го изпрати отново.',
    unavailableHead: 'Плащанията в момента не са достъпни',
    unavailableBody: 'Платежният процесор не е конфигуриран. Не може да бъде прието плащане и нищо не е удържано.',
    errorHead: 'Възникна грешка',
    loading: 'Зареждане…',
    language: 'Език',
    poweredBy: 'Плащанията се обработват от Stripe. Данните на картата се въвеждат директно в Stripe и не достигат сървърите на WyoCSPay.',
    terms: 'Общи условия',
    privacy: 'Политика за поверителност',
    support: 'Поддръжка',
    acceptPre: 'Прочетох и приемам',
    acceptMid: 'и',
    acceptPost: '.',
    acceptRequired: 'Моля, приемете условията, преди да платите.',
    sellerNote: 'Плащате на посочения по-горе търговец. WyoCSPay обработва плащането и не е продавачът.',
    operatedBy: 'Страницата се управлява от',
    rights: 'Всички права запазени.',
    receiptNote: 'Запазете референцията по-горе, ако Ви се наложи да се свържете с поддръжката.',
    advertisement: 'Реклама',
    adNote: 'Платена реклама. Няма връзка с Вашето плащане и се отваря в нов прозорец.',
    landingHead: 'Защитена страница за плащане',
    landingBody: 'Тук се извършват плащанията през WyoCSPay. На този адрес няма какво да въвеждате: плащането се отваря от линка, който търговецът Ви изпраща.',
    landingAsk: 'Ако очаквате да платите на някого, помолете го да Ви изпрати линка за плащане отново.',
    assuranceLabel: 'Сигурност и съответствие',
    assuranceCardsHead: 'Данни на картата',
    assuranceCards: 'Формата за карта на тази страница се обслужва от Stripe. Това, което въвеждате в нея, отива при Stripe, а не при нас: WyoCSPay не получава и не съхранява номера на картата Ви.',
    assuranceStandardHead: 'Оценка',
    assuranceStandardNote: 'Стандартът, спрямо който е оценена тази страница за плащане. Нивото SAQ A е предвидено за страница, която оставя цялата обработка на картови данни на валидиран доставчик.',
    assuranceScanHead: 'Външно сканиране',
    assuranceScan: 'Системите, които обслужват тази страница, се сканират за уязвимости на всеки три месеца от сканиращ доставчик, одобрен от PCI Security Standards Council.',
    assuranceScanLast: 'Последното сканиране е преминато успешно',
    assuranceTlsHead: 'Връзка',
    assuranceTls: 'Страницата се доставя по TLS 1.2 или 1.3, само с удостоверено шифроване.',
    assuranceClose: 'Затвори',
    stepMethod: 'Начин на плащане',
    stepConfirm: 'Потвърждение',
    stepResult: 'Резултат',
    txnLabel: 'Транзакция №',
    sessionLabel: 'Сесията изтича след',
    sessionExpired: 'Сесията изтече',
    sessionExpiredNote: 'Тази платежна сесия изтече и линкът вече не може да се използва. Моля, поискайте нов линк за плащане.',
    payRequest: 'Заявка за плащане',
    invoiceNo: 'Номер на фактура',
    orderSummary: 'Обобщение на поръчката',
    sumSubtotal: 'Междинна сума',
    totalDue: 'Общо за плащане',
    continueBtn: 'Продължи',
    changeMethod: 'Смени начина',
    reviewLine: 'Моля, прегледайте и потвърдете плащането.',
    holdCard: 'Сумата се оторизира веднага; таксата за обработка се удържа, след като банката Ви одобри плащането.',
    resultBand: 'Плащане към търговеца',
    payInfo: 'Информация за плащането',
    statusLabel: 'Статус',
    chargedLabel: 'Удържана сума',
    chargedLabelNo: 'Заявена сума',
    pendingHead: 'Обработваме плащането Ви',
    pendingSub: 'Моля, дръжте този прозорец отворен - не презареждайте и не затваряйте.',
    pendingKeep: 'Моля, дръжте този прозорец отворен - не презареждайте и не затваряйте.',
    stepSubmitted: 'Плащането е изпратено',
    stepAuthorized: 'Оторизирано от банката Ви',
    stepConfirmed: 'Потвърдено от WyoCSPay',
    okHint: 'Разписка е изпратена на имейла Ви. Можете спокойно да се върнете при търговеца.',
    chooseAnother: 'Изберете друг начин',
    securedTitle: 'Защитено от WyoCSPay',
    securedBody: 'Обработено през криптирана връзка по PCI DSS Level 1. Данните на картата Ви са въведени директно в банката и никога не са докосвали тази страница.',
    supportQ: 'Въпроси за това плащане? Свържете се с поддръжката на',
    timeoutHead: 'Все още потвърждаваме плащането Ви',
    timeoutSub: 'Това отнема повече от обичайното. Плащането Ви не е изгубено - можете да проверите отново по-долу.',
    timeoutHint: 'Все още нямаме потвърждение от банката Ви. Това може да отнеме няколко минути. Проверете отново сега или ще получите имейл веднага щом се финализира - не е нужно да плащате повторно.',
    checkAgain: 'Провери отново',
    trust3dsTitle: '3-D Secure 2.2',
    trust3dsBody: 'Всяко плащане се автентикира директно с банката-издател.',
    trustPciTitle: 'PCI DSS Level 1',
    trustPciBody: 'Данните на картата се токенизират и никога не се съхраняват на нашите сървъри.',
    trust247Title: 'Денонощна поддръжка',
    trust247Body: 'Екипът ни отговаря на всяко запитване за транзакция в рамките на два часа.',
    subPackageTitle: 'Вашият пакет WyoCSPay',
    account: 'Акаунт',
    packageLbl: 'Пакет',
    billingLbl: 'Фактуриране',
    billingRenewal: 'Месечно подновяване',
    billingSetup: 'Първи месец (настройка)',
    subAuthNote: 'С плащането разрешавате месечни удръжки за този пакет, докато не го откажете.',
    subPaidTo: 'Плаща се на Wyoming Cloud Solutions LLC през Stripe.',
  },
  de: {
    secure: 'Sichere Verbindung',
    merchant: 'Händler',
    reference: 'Referenz',
    description: 'Beschreibung',
    amount: 'Betrag',
    total: 'Zu zahlen',
    summary: 'Übersicht',
    payNow: 'Bezahlen',
    paying: 'Wird gesendet…',
    chooseMethod: 'Zahlungsart wählen',
    methodsNote: 'Welche Zahlungsarten erscheinen, hängt von Ihrer Bank, Ihrem Land und dem Konto des Händlers ab.',
    unavailableTitle: 'Hier nicht angeboten',
    confirmingHead: 'Wir bestätigen Ihre Zahlung',
    confirmingBody: 'Wir warten auf die Bestätigung des Zahlungsnetzwerks. Bitte lassen Sie dieses Fenster geöffnet.',
    confirmingWhy: 'Ihre Bank wurde kontaktiert. Eine Zahlung gilt erst als abgeschlossen, wenn das Zahlungsnetzwerk sie direkt an unsere Server bestätigt - nicht wenn Ihr Browser zurückkehrt.',
    okHead: 'Zahlung bestätigt',
    okBody: 'wurde bezahlt.',
    failHead: 'Zahlung abgelehnt',
    failBody: 'Ihre Bank hat die Zahlung nicht autorisiert. Es wurde kein Geld abgebucht.',
    failHint: 'Wenn das erneut passiert, wenden Sie sich an Ihre Bank oder wählen Sie eine andere Zahlungsart.',
    tryAgain: 'Erneut versuchen',
    amountCharged: 'Belasteter Betrag',
    amountAttempted: 'Versuchter Betrag',
    paidAt: 'Bestätigt am',
    method: 'Zahlungsart',
    expiredHead: 'Dieser Zahlungslink ist abgelaufen',
    expiredBody: 'Bitten Sie den Händler um einen neuen Link.',
    revokedHead: 'Dieser Zahlungslink wurde storniert',
    revokedBody: 'Wenden Sie sich an den Händler, falls dies ein Fehler ist.',
    notFoundHead: 'Dieser Zahlungslink ist ungültig',
    notFoundBody: 'Prüfen Sie den Link oder bitten Sie den Händler, ihn erneut zu senden.',
    unavailableHead: 'Zahlungen sind derzeit nicht möglich',
    unavailableBody: 'Der Zahlungsdienstleister ist nicht konfiguriert. Es kann keine Zahlung erfolgen; nichts wurde belastet.',
    errorHead: 'Etwas ist schiefgelaufen',
    loading: 'Wird geladen…',
    language: 'Sprache',
    poweredBy: 'Zahlungen werden von Stripe abgewickelt. Kartendaten werden direkt an Stripe übermittelt und erreichen die Server von WyoCSPay nie.',
    terms: 'Nutzungsbedingungen',
    privacy: 'Datenschutzhinweis',
    support: 'Support',
    acceptPre: 'Ich habe gelesen und akzeptiere die',
    acceptMid: 'und den',
    acceptPost: '.',
    acceptRequired: 'Bitte akzeptieren Sie die Bedingungen vor der Zahlung.',
    sellerNote: 'Sie zahlen an den oben genannten Händler. WyoCSPay wickelt die Zahlung ab und ist nicht der Verkäufer.',
    operatedBy: 'Zahlungsseite betrieben von',
    rights: 'Alle Rechte vorbehalten.',
    receiptNote: 'Bewahren Sie die obige Referenz auf, falls Sie den Support kontaktieren müssen.',
    advertisement: 'Anzeige',
    adNote: 'Bezahlte Werbung. Sie hat nichts mit Ihrer Zahlung zu tun und öffnet sich in einem neuen Fenster.',
    landingHead: 'Sichere Zahlungsseite',
    landingBody: 'Hier werden Zahlungen über WyoCSPay ausgeführt. Auf dieser Adresse gibt es nichts einzugeben: Eine Zahlung öffnet sich über den Link, den Ihnen der Händler sendet.',
    landingAsk: 'Wenn Sie eine Zahlung erwarten, bitten Sie den Händler, Ihnen den Zahlungslink erneut zu senden.',
    assuranceLabel: 'Sicherheit und Compliance',
    assuranceCardsHead: 'Kartendaten',
    assuranceCards: 'Das Kartenformular auf dieser Seite wird von Stripe bereitgestellt. Was Sie dort eingeben, geht an Stripe und nicht an uns: WyoCSPay erhält und speichert Ihre Kartennummer nie.',
    assuranceStandardHead: 'Bewertung',
    assuranceStandardNote: 'Der Standard, nach dem diese Zahlungsseite bewertet wird. Die Stufe SAQ A ist für eine Seite vorgesehen, die die gesamte Kartenverarbeitung einem validierten Anbieter überlässt.',
    assuranceScanHead: 'Externe Scans',
    assuranceScan: 'Die Systeme hinter dieser Seite werden alle drei Monate von einem vom PCI Security Standards Council zugelassenen Scanning-Anbieter auf Schwachstellen geprüft.',
    assuranceScanLast: 'Letzter Scan bestanden',
    assuranceTlsHead: 'Verbindung',
    assuranceTls: 'Diese Seite wird über TLS 1.2 oder 1.3 ausgeliefert, ausschließlich mit authentifizierter Verschlüsselung.',
    assuranceClose: 'Schließen',
    stepMethod: 'Zahlungsart',
    stepConfirm: 'Bestätigung',
    stepResult: 'Ergebnis',
    txnLabel: 'Transaktionsnr.',
    sessionLabel: 'Sitzung läuft ab in',
    sessionExpired: 'Sitzung abgelaufen',
    sessionExpiredNote: 'Diese Zahlungssitzung ist abgelaufen und der Link kann nicht mehr verwendet werden. Bitte fordern Sie einen neuen Zahlungslink an.',
    payRequest: 'Zahlungsanforderung',
    invoiceNo: 'Rechnungsnummer',
    orderSummary: 'Übersicht',
    sumSubtotal: 'Zwischensumme',
    totalDue: 'Zu zahlen',
    continueBtn: 'Weiter',
    changeMethod: 'Zahlungsart ändern',
    reviewLine: 'Bitte prüfen und bestätigen Sie Ihre Zahlung.',
    holdCard: 'Der Betrag wird sofort autorisiert; die Bearbeitungsgebühr wird erhoben, sobald Ihre Bank die Zahlung freigibt.',
    resultBand: 'Zahlung an den Händler',
    payInfo: 'Zahlungsinformationen',
    statusLabel: 'Status',
    chargedLabel: 'Belasteter Betrag',
    chargedLabelNo: 'Versuchter Betrag',
    pendingHead: 'Ihre Zahlung wird verarbeitet',
    pendingSub: 'Bitte lassen Sie dieses Fenster geöffnet - nicht aktualisieren oder schließen.',
    pendingKeep: 'Bitte lassen Sie dieses Fenster geöffnet - nicht aktualisieren oder schließen.',
    stepSubmitted: 'Zahlung übermittelt',
    stepAuthorized: 'Von Ihrer Bank autorisiert',
    stepConfirmed: 'Von WyoCSPay bestätigt',
    okHint: 'Ein Beleg wurde Ihnen per E-Mail gesendet. Sie können sicher zum Händler zurückkehren.',
    chooseAnother: 'Andere Zahlungsart wählen',
    securedTitle: 'Gesichert durch WyoCSPay',
    securedBody: 'Verarbeitet über eine verschlüsselte PCI-DSS-Level-1-Verbindung. Ihre Kartendaten wurden direkt bei der Bank eingegeben und haben diese Seite nie berührt.',
    supportQ: 'Fragen zu dieser Zahlung? Wenden Sie sich an den Support unter',
    timeoutHead: 'Wir bestätigen Ihre Zahlung noch',
    timeoutSub: 'Das dauert länger als üblich. Ihre Zahlung ist nicht verloren - Sie können unten erneut prüfen.',
    timeoutHint: 'Wir haben noch keine Bestätigung Ihrer Bank. Das kann einige Minuten dauern. Prüfen Sie jetzt erneut, oder Sie erhalten eine E-Mail, sobald es abgeschlossen ist - eine erneute Zahlung ist nicht nötig.',
    checkAgain: 'Erneut prüfen',
    trust3dsTitle: '3-D Secure 2.2',
    trust3dsBody: 'Jede Zahlung wird direkt mit Ihrer Bank authentifiziert.',
    trustPciTitle: 'PCI DSS Level 1',
    trustPciBody: 'Kartendaten werden tokenisiert und nie auf unseren Servern gespeichert.',
    trust247Title: 'Rund-um-die-Uhr-Support',
    trust247Body: 'Unser Team beantwortet jede Transaktionsanfrage innerhalb von zwei Stunden.',
    subPackageTitle: 'Ihr WyoCSPay-Paket',
    account: 'Konto',
    packageLbl: 'Paket',
    billingLbl: 'Abrechnung',
    billingRenewal: 'Monatliche Verlängerung',
    billingSetup: 'Erster Monat (Einrichtung)',
    subAuthNote: 'Mit der Zahlung autorisieren Sie monatliche Abbuchungen für dieses Paket, bis Sie es kündigen.',
    subPaidTo: 'Zahlung an Wyoming Cloud Solutions LLC über Stripe.',
  },
  fr: {
    secure: 'Connexion sécurisée',
    merchant: 'Marchand',
    reference: 'Référence',
    description: 'Description',
    amount: 'Montant',
    total: 'Total à payer',
    summary: 'Récapitulatif',
    payNow: 'Payer',
    paying: 'Envoi…',
    chooseMethod: 'Choisissez votre moyen de paiement',
    methodsNote: 'Les moyens disponibles dépendent de votre banque, de votre pays et du compte du marchand.',
    unavailableTitle: 'Non proposé ici',
    confirmingHead: 'Confirmation de votre paiement',
    confirmingBody: 'Nous attendons la confirmation du réseau de paiement. Gardez cette fenêtre ouverte.',
    confirmingWhy: 'Votre banque a été contactée. Un paiement n’est validé que lorsque le réseau le confirme directement à nos serveurs, et non au retour de votre navigateur.',
    okHead: 'Paiement confirmé',
    okBody: 'a été payé.',
    failHead: 'Paiement refusé',
    failBody: 'Votre banque n’a pas autorisé ce paiement. Aucun montant n’a été débité.',
    failHint: 'Si cela se reproduit, contactez votre banque ou essayez un autre moyen de paiement.',
    tryAgain: 'Réessayer',
    amountCharged: 'Montant débité',
    amountAttempted: 'Montant tenté',
    paidAt: 'Confirmé le',
    method: 'Moyen de paiement',
    expiredHead: 'Ce lien de paiement a expiré',
    expiredBody: 'Demandez un nouveau lien au marchand.',
    revokedHead: 'Ce lien de paiement a été annulé',
    revokedBody: 'Contactez le marchand si vous pensez qu’il s’agit d’une erreur.',
    notFoundHead: 'Ce lien de paiement n’est pas valide',
    notFoundBody: 'Vérifiez le lien ou demandez au marchand de le renvoyer.',
    unavailableHead: 'Les paiements sont indisponibles',
    unavailableBody: 'Le prestataire de paiement n’est pas configuré. Aucun paiement ne peut être pris et rien n’a été débité.',
    errorHead: 'Une erreur s’est produite',
    loading: 'Chargement…',
    language: 'Langue',
    poweredBy: 'Paiements traités par Stripe. Les données de carte sont saisies directement dans Stripe et n’atteignent jamais les serveurs WyoCSPay.',
    terms: 'Conditions générales',
    privacy: 'Politique de confidentialité',
    support: 'Assistance',
    acceptPre: 'J’ai lu et j’accepte les',
    acceptMid: 'et la',
    acceptPost: '.',
    acceptRequired: 'Veuillez accepter les conditions avant de payer.',
    sellerNote: 'Vous payez le marchand indiqué ci-dessus. WyoCSPay traite le paiement et n’est pas le vendeur.',
    operatedBy: 'Page de paiement exploitée par',
    rights: 'Tous droits réservés.',
    receiptNote: 'Conservez la référence ci-dessus si vous devez contacter l’assistance.',
    advertisement: 'Publicité',
    adNote: 'Publicité payante. Elle n’a aucun rapport avec votre paiement et s’ouvre dans une nouvelle fenêtre.',
    landingHead: 'Page de paiement sécurisée',
    landingBody: 'C’est ici que se règlent les paiements WyoCSPay. Il n’y a rien à saisir à cette adresse : un paiement s’ouvre depuis le lien que le marchand vous envoie.',
    landingAsk: 'Si vous attendez de régler quelque chose, demandez au marchand de vous renvoyer le lien de paiement.',
    assuranceLabel: 'Sécurité et conformité',
    assuranceCardsHead: 'Données de carte',
    assuranceCards: 'Le formulaire de carte de cette page est fourni par Stripe. Ce que vous y saisissez va à Stripe, pas à nous : WyoCSPay ne reçoit ni ne conserve jamais votre numéro de carte.',
    assuranceStandardHead: 'Évaluation',
    assuranceStandardNote: 'La norme selon laquelle cette page de paiement est évaluée. Le niveau SAQ A est prévu pour une page qui confie tout le traitement des cartes à un prestataire validé.',
    assuranceScanHead: 'Analyses externes',
    assuranceScan: 'Les systèmes qui servent cette page font l’objet d’une analyse de vulnérabilités tous les trois mois, par un prestataire agréé par le PCI Security Standards Council.',
    assuranceScanLast: 'Dernière analyse réussie',
    assuranceTlsHead: 'Connexion',
    assuranceTls: 'Cette page est servie en TLS 1.2 ou 1.3, avec chiffrement authentifié uniquement.',
    assuranceClose: 'Fermer',
    stepMethod: 'Moyen de paiement',
    stepConfirm: 'Confirmation',
    stepResult: 'Résultat',
    txnLabel: 'Transaction n°',
    sessionLabel: 'La session expire dans',
    sessionExpired: 'Session expirée',
    sessionExpiredNote: 'Cette session de paiement a expiré et ce lien ne peut plus être utilisé. Veuillez demander un nouveau lien de paiement.',
    payRequest: 'Demande de paiement',
    invoiceNo: 'Numéro de facture',
    orderSummary: 'Récapitulatif',
    sumSubtotal: 'Sous-total',
    totalDue: 'Total à payer',
    continueBtn: 'Continuer',
    changeMethod: 'Changer de moyen',
    reviewLine: 'Veuillez vérifier et confirmer votre paiement.',
    holdCard: 'Le montant est autorisé immédiatement ; les frais de traitement sont prélevés une fois que votre banque approuve le paiement.',
    resultBand: 'Paiement au marchand',
    payInfo: 'Informations de paiement',
    statusLabel: 'Statut',
    chargedLabel: 'Montant débité',
    chargedLabelNo: 'Montant tenté',
    pendingHead: 'Traitement de votre paiement',
    pendingSub: 'Veuillez garder cette fenêtre ouverte - ne pas actualiser ni fermer.',
    pendingKeep: 'Veuillez garder cette fenêtre ouverte - ne pas actualiser ni fermer.',
    stepSubmitted: 'Paiement envoyé',
    stepAuthorized: 'Autorisé par votre banque',
    stepConfirmed: 'Confirmé par WyoCSPay',
    okHint: 'Un reçu vous a été envoyé par e-mail. Vous pouvez revenir en toute sécurité chez le marchand.',
    chooseAnother: 'Choisir un autre moyen',
    securedTitle: 'Sécurisé par WyoCSPay',
    securedBody: 'Traité via une connexion chiffrée PCI DSS Level 1. Les données de votre carte ont été saisies directement auprès de la banque et n’ont jamais touché cette page.',
    supportQ: 'Des questions sur ce paiement ? Contactez l’assistance à',
    timeoutHead: 'Nous confirmons encore votre paiement',
    timeoutSub: 'Cela prend plus de temps que d’habitude. Votre paiement n’est pas perdu - vous pouvez vérifier à nouveau ci-dessous.',
    timeoutHint: 'Nous n’avons pas encore de confirmation de votre banque. Cela peut prendre quelques minutes. Vérifiez à nouveau maintenant, ou vous recevrez un e-mail dès que ce sera réglé - inutile de payer à nouveau.',
    checkAgain: 'Vérifier à nouveau',
    trust3dsTitle: '3-D Secure 2.2',
    trust3dsBody: 'Chaque paiement est authentifié directement auprès de votre banque émettrice.',
    trustPciTitle: 'PCI DSS Level 1',
    trustPciBody: 'Les données de carte sont tokenisées et jamais stockées sur nos serveurs.',
    trust247Title: 'Assistance 24h/24',
    trust247Body: 'Notre équipe répond à chaque demande de transaction sous deux heures.',
    subPackageTitle: 'Votre offre WyoCSPay',
    account: 'Compte',
    packageLbl: 'Offre',
    billingLbl: 'Facturation',
    billingRenewal: 'Renouvellement mensuel',
    billingSetup: 'Premier mois (mise en place)',
    subAuthNote: 'En payant, vous autorisez des prélèvements mensuels pour cette offre jusqu’à son annulation.',
    subPaidTo: 'Payé à Wyoming Cloud Solutions LLC via Stripe.',
  },
  es: {
    secure: 'Conexión segura',
    merchant: 'Comercio',
    reference: 'Referencia',
    description: 'Descripción',
    amount: 'Importe',
    total: 'Total a pagar',
    summary: 'Resumen',
    payNow: 'Pagar',
    paying: 'Enviando…',
    chooseMethod: 'Elija cómo pagar',
    methodsNote: 'Los métodos disponibles dependen de su banco, su país y la cuenta del comercio.',
    unavailableTitle: 'No disponible aquí',
    confirmingHead: 'Confirmando su pago',
    confirmingBody: 'Esperamos la confirmación de la red de pagos. Mantenga esta ventana abierta.',
    confirmingWhy: 'Se ha contactado con su banco. Un pago se marca como completado solo cuando la red lo confirma directamente a nuestros servidores.',
    okHead: 'Pago confirmado',
    okBody: 'ha recibido el pago.',
    failHead: 'Pago rechazado',
    failBody: 'Su banco no autorizó el cargo. No se ha retirado dinero de su cuenta.',
    failHint: 'Si vuelve a ocurrir, contacte con su banco o pruebe otro método de pago.',
    tryAgain: 'Intentar de nuevo',
    amountCharged: 'Importe cobrado',
    amountAttempted: 'Importe intentado',
    paidAt: 'Confirmado el',
    method: 'Método de pago',
    expiredHead: 'Este enlace de pago ha caducado',
    expiredBody: 'Pida al comercio que le envíe uno nuevo.',
    revokedHead: 'Este enlace de pago fue cancelado',
    revokedBody: 'Contacte con el comercio si cree que es un error.',
    notFoundHead: 'Este enlace de pago no es válido',
    notFoundBody: 'Compruebe el enlace o pida al comercio que lo reenvíe.',
    unavailableHead: 'Los pagos no están disponibles',
    unavailableBody: 'El procesador de pagos no está configurado. No se puede cobrar y no se ha realizado ningún cargo.',
    errorHead: 'Algo salió mal',
    loading: 'Cargando…',
    language: 'Idioma',
    poweredBy: 'Pagos procesados por Stripe. Los datos de la tarjeta se introducen directamente en Stripe y nunca llegan a los servidores de WyoCSPay.',
    terms: 'Términos del servicio',
    privacy: 'Aviso de privacidad',
    support: 'Soporte',
    acceptPre: 'He leído y acepto los',
    acceptMid: 'y el',
    acceptPost: '.',
    acceptRequired: 'Acepte los términos antes de pagar.',
    sellerNote: 'Está pagando al comercio indicado arriba. WyoCSPay procesa el pago y no es el vendedor.',
    operatedBy: 'Página de pago operada por',
    rights: 'Todos los derechos reservados.',
    receiptNote: 'Guarde la referencia anterior por si necesita contactar con soporte.',
    advertisement: 'Publicidad',
    adNote: 'Publicidad pagada. No tiene relación con su pago y se abre en una ventana nueva.',
    landingHead: 'Página de pago segura',
    landingBody: 'Aquí se realizan los pagos de WyoCSPay. En esta dirección no hay nada que introducir: el pago se abre desde el enlace que le envía el comercio.',
    landingAsk: 'Si espera pagar a alguien, pídale que le envíe de nuevo el enlace de pago.',
    assuranceLabel: 'Seguridad y cumplimiento',
    assuranceCardsHead: 'Datos de la tarjeta',
    assuranceCards: 'El formulario de tarjeta de esta página lo sirve Stripe. Lo que escribe en él va a Stripe, no a nosotros: WyoCSPay nunca recibe ni almacena su número de tarjeta.',
    assuranceStandardHead: 'Evaluación',
    assuranceStandardNote: 'La norma con la que se evalúa esta página de pago. El nivel SAQ A está previsto para una página que deja todo el tratamiento de tarjetas en manos de un proveedor validado.',
    assuranceScanHead: 'Análisis externos',
    assuranceScan: 'Los sistemas que sirven esta página se analizan en busca de vulnerabilidades cada tres meses, a cargo de un proveedor aprobado por el PCI Security Standards Council.',
    assuranceScanLast: 'El último análisis se superó',
    assuranceTlsHead: 'Conexión',
    assuranceTls: 'Esta página se sirve mediante TLS 1.2 o 1.3, solo con cifrado autenticado.',
    assuranceClose: 'Cerrar',
    stepMethod: 'Método de pago',
    stepConfirm: 'Confirmación',
    stepResult: 'Resultado',
    txnLabel: 'N.º de transacción',
    sessionLabel: 'La sesión expira en',
    sessionExpired: 'Sesión expirada',
    sessionExpiredNote: 'Esta sesión de pago ha expirado y este enlace ya no se puede usar. Solicite un nuevo enlace de pago.',
    payRequest: 'Solicitud de pago',
    invoiceNo: 'Número de factura',
    orderSummary: 'Resumen',
    sumSubtotal: 'Subtotal',
    totalDue: 'Total a pagar',
    continueBtn: 'Continuar',
    changeMethod: 'Cambiar método',
    reviewLine: 'Revise y confirme su pago.',
    holdCard: 'El importe se autoriza de inmediato; la comisión de procesamiento se cobra una vez que su banco aprueba el cargo.',
    resultBand: 'Pago al comercio',
    payInfo: 'Información del pago',
    statusLabel: 'Estado',
    chargedLabel: 'Importe cobrado',
    chargedLabelNo: 'Importe intentado',
    pendingHead: 'Procesando su pago',
    pendingSub: 'Mantenga esta ventana abierta - no la actualice ni la cierre.',
    pendingKeep: 'Mantenga esta ventana abierta - no la actualice ni la cierre.',
    stepSubmitted: 'Pago enviado',
    stepAuthorized: 'Autorizado por su banco',
    stepConfirmed: 'Confirmado por WyoCSPay',
    okHint: 'Se le ha enviado un recibo por correo. Puede volver al comercio con seguridad.',
    chooseAnother: 'Elegir otro método',
    securedTitle: 'Protegido por WyoCSPay',
    securedBody: 'Procesado a través de una conexión cifrada PCI DSS Level 1. Los datos de su tarjeta se introdujeron directamente en el banco y nunca tocaron esta página.',
    supportQ: '¿Preguntas sobre este cargo? Contacte con soporte en',
    timeoutHead: 'Aún confirmando su pago',
    timeoutSub: 'Esto está tardando más de lo habitual. Su pago no se ha perdido - puede volver a comprobarlo abajo.',
    timeoutHint: 'Aún no tenemos confirmación de su banco. Esto puede tardar unos minutos. Compruebe de nuevo ahora, o recibirá un correo en cuanto se liquide - no es necesario volver a pagar.',
    checkAgain: 'Comprobar de nuevo',
    trust3dsTitle: '3-D Secure 2.2',
    trust3dsBody: 'Cada cargo se autentica directamente con su banco emisor.',
    trustPciTitle: 'PCI DSS Level 1',
    trustPciBody: 'Los datos de la tarjeta se tokenizan y nunca se almacenan en nuestros servidores.',
    trust247Title: 'Soporte ininterrumpido',
    trust247Body: 'Nuestro equipo responde cada consulta de transacción en un plazo de dos horas.',
    subPackageTitle: 'Su paquete WyoCSPay',
    account: 'Cuenta',
    packageLbl: 'Paquete',
    billingLbl: 'Facturación',
    billingRenewal: 'Renovación mensual',
    billingSetup: 'Primer mes (configuración)',
    subAuthNote: 'Al pagar, autoriza cargos mensuales por este paquete hasta que lo cancele.',
    subPaidTo: 'Pagado a Wyoming Cloud Solutions LLC a través de Stripe.',
  },
  it: {
    secure: 'Connessione sicura',
    merchant: 'Esercente',
    reference: 'Riferimento',
    description: 'Descrizione',
    amount: 'Importo',
    total: 'Totale da pagare',
    summary: 'Riepilogo',
    payNow: 'Paga',
    paying: 'Invio…',
    chooseMethod: 'Scegli come pagare',
    methodsNote: 'I metodi disponibili dipendono dalla tua banca, dal tuo paese e dal conto dell’esercente.',
    unavailableTitle: 'Non disponibile qui',
    confirmingHead: 'Stiamo confermando il pagamento',
    confirmingBody: 'Attendiamo la conferma dal circuito di pagamento. Non chiudere questa finestra.',
    confirmingWhy: 'La tua banca è stata contattata. Un pagamento risulta completato solo quando il circuito lo conferma direttamente ai nostri server.',
    okHead: 'Pagamento confermato',
    okBody: 'è stato pagato.',
    failHead: 'Pagamento rifiutato',
    failBody: 'La tua banca non ha autorizzato l’addebito. Nessun importo è stato prelevato.',
    failHint: 'Se accade di nuovo, contatta la tua banca o prova un altro metodo.',
    tryAgain: 'Riprova',
    amountCharged: 'Importo addebitato',
    amountAttempted: 'Importo tentato',
    paidAt: 'Confermato il',
    method: 'Metodo di pagamento',
    expiredHead: 'Questo link di pagamento è scaduto',
    expiredBody: 'Chiedi all’esercente un nuovo link.',
    revokedHead: 'Questo link è stato annullato',
    revokedBody: 'Contatta l’esercente se ritieni sia un errore.',
    notFoundHead: 'Questo link non è valido',
    notFoundBody: 'Controlla il link o chiedi all’esercente di reinviarlo.',
    unavailableHead: 'I pagamenti non sono disponibili',
    unavailableBody: 'Il processore di pagamento non è configurato. Nessun pagamento è possibile e nulla è stato addebitato.',
    errorHead: 'Qualcosa è andato storto',
    loading: 'Caricamento…',
    language: 'Lingua',
    poweredBy: 'Pagamenti elaborati da Stripe. I dati della carta vengono inseriti direttamente in Stripe e non raggiungono mai i server WyoCSPay.',
    terms: 'Termini di servizio',
    privacy: 'Informativa sulla privacy',
    support: 'Assistenza',
    acceptPre: 'Ho letto e accetto i',
    acceptMid: 'e l’',
    acceptPost: '.',
    acceptRequired: 'Accetta i termini prima di pagare.',
    sellerNote: 'Stai pagando l’esercente indicato sopra. WyoCSPay elabora il pagamento e non è il venditore.',
    operatedBy: 'Pagina di pagamento gestita da',
    rights: 'Tutti i diritti riservati.',
    receiptNote: 'Conserva il riferimento sopra se devi contattare l’assistenza.',
    advertisement: 'Pubblicità',
    adNote: 'Pubblicità a pagamento. Non riguarda il tuo pagamento e si apre in una nuova finestra.',
    landingHead: 'Pagina di pagamento sicura',
    landingBody: 'Qui si effettuano i pagamenti WyoCSPay. A questo indirizzo non c’è nulla da inserire: il pagamento si apre dal link che ti invia l’esercente.',
    landingAsk: 'Se stai aspettando di pagare qualcuno, chiedigli di inviarti di nuovo il link di pagamento.',
    assuranceLabel: 'Sicurezza e conformità',
    assuranceCardsHead: 'Dati della carta',
    assuranceCards: 'Il modulo della carta su questa pagina è fornito da Stripe. Quello che ci scrivi va a Stripe, non a noi: WyoCSPay non riceve e non conserva mai il numero della tua carta.',
    assuranceStandardHead: 'Valutazione',
    assuranceStandardNote: 'Lo standard rispetto al quale questa pagina di pagamento è valutata. Il livello SAQ A è previsto per una pagina che lascia tutta la gestione delle carte a un fornitore validato.',
    assuranceScanHead: 'Scansioni esterne',
    assuranceScan: 'I sistemi che servono questa pagina vengono sottoposti a scansione delle vulnerabilità ogni tre mesi da un fornitore approvato dal PCI Security Standards Council.',
    assuranceScanLast: 'Ultima scansione superata',
    assuranceTlsHead: 'Connessione',
    assuranceTls: 'Questa pagina viene servita su TLS 1.2 o 1.3, solo con cifratura autenticata.',
    assuranceClose: 'Chiudi',
    stepMethod: 'Metodo di pagamento',
    stepConfirm: 'Conferma',
    stepResult: 'Esito',
    txnLabel: 'N. transazione',
    sessionLabel: 'La sessione scade tra',
    sessionExpired: 'Sessione scaduta',
    sessionExpiredNote: 'Questa sessione di pagamento è scaduta e il link non può più essere utilizzato. Richiedi un nuovo link di pagamento.',
    payRequest: 'Richiesta di pagamento',
    invoiceNo: 'Numero fattura',
    orderSummary: 'Riepilogo',
    sumSubtotal: 'Subtotale',
    totalDue: 'Totale da pagare',
    continueBtn: 'Continua',
    changeMethod: 'Cambia metodo',
    reviewLine: 'Controlla e conferma il pagamento.',
    holdCard: 'L’importo viene autorizzato subito; la commissione di elaborazione viene addebitata una volta che la tua banca approva l’addebito.',
    resultBand: 'Pagamento all’esercente',
    payInfo: 'Informazioni sul pagamento',
    statusLabel: 'Stato',
    chargedLabel: 'Importo addebitato',
    chargedLabelNo: 'Importo tentato',
    pendingHead: 'Elaborazione del pagamento',
    pendingSub: 'Tieni aperta questa finestra - non aggiornare né chiudere.',
    pendingKeep: 'Tieni aperta questa finestra - non aggiornare né chiudere.',
    stepSubmitted: 'Pagamento inviato',
    stepAuthorized: 'Autorizzato dalla tua banca',
    stepConfirmed: 'Confermato da WyoCSPay',
    okHint: 'Ti abbiamo inviato una ricevuta via e-mail. Puoi tornare all’esercente in sicurezza.',
    chooseAnother: 'Scegli un altro metodo',
    securedTitle: 'Protetto da WyoCSPay',
    securedBody: 'Elaborato tramite una connessione cifrata PCI DSS Level 1. I dati della tua carta sono stati inseriti direttamente presso la banca e non hanno mai toccato questa pagina.',
    supportQ: 'Domande su questo addebito? Contatta l’assistenza a',
    timeoutHead: 'Stiamo ancora confermando il pagamento',
    timeoutSub: 'Sta richiedendo più tempo del solito. Il pagamento non è perso - puoi controllare di nuovo qui sotto.',
    timeoutHint: 'Non abbiamo ancora la conferma della tua banca. Può richiedere qualche minuto. Controlla di nuovo ora, oppure riceverai un’e-mail non appena viene completato - non serve pagare di nuovo.',
    checkAgain: 'Controlla di nuovo',
    trust3dsTitle: '3-D Secure 2.2',
    trust3dsBody: 'Ogni addebito è autenticato direttamente con la tua banca emittente.',
    trustPciTitle: 'PCI DSS Level 1',
    trustPciBody: 'I dati della carta sono tokenizzati e mai memorizzati sui nostri server.',
    trust247Title: 'Assistenza 24 ore su 24',
    trust247Body: 'Il nostro team risponde a ogni richiesta sulle transazioni entro due ore.',
    subPackageTitle: 'Il tuo pacchetto WyoCSPay',
    account: 'Account',
    packageLbl: 'Pacchetto',
    billingLbl: 'Fatturazione',
    billingRenewal: 'Rinnovo mensile',
    billingSetup: 'Primo mese (attivazione)',
    subAuthNote: 'Pagando autorizzi addebiti mensili per questo pacchetto fino all’annullamento.',
    subPaidTo: 'Pagato a Wyoming Cloud Solutions LLC tramite Stripe.',
  },
  ru: {
    secure: 'Защищённое соединение',
    merchant: 'Продавец',
    reference: 'Ссылка',
    description: 'Описание',
    amount: 'Сумма',
    total: 'Итого к оплате',
    summary: 'Сводка заказа',
    payNow: 'Оплатить',
    paying: 'Отправка…',
    chooseMethod: 'Выберите способ оплаты',
    methodsNote: 'Доступные способы зависят от вашего банка, страны и счёта продавца.',
    unavailableTitle: 'Здесь не предлагается',
    confirmingHead: 'Подтверждаем ваш платёж',
    confirmingBody: 'Ожидаем подтверждения от платёжной сети. Не закрывайте и не обновляйте страницу.',
    confirmingWhy: 'Ваш банк уведомлён. Платёж считается завершённым только когда платёжная сеть подтвердит его напрямую нашим серверам.',
    okHead: 'Платёж подтверждён',
    okBody: 'получил оплату.',
    failHead: 'Платёж отклонён',
    failBody: 'Ваш банк не авторизовал списание. Средства не списаны.',
    failHint: 'Если это повторится, обратитесь в банк или выберите другой способ оплаты.',
    tryAgain: 'Попробовать снова',
    amountCharged: 'Списанная сумма',
    amountAttempted: 'Запрошенная сумма',
    paidAt: 'Подтверждено',
    method: 'Способ оплаты',
    expiredHead: 'Срок действия ссылки истёк',
    expiredBody: 'Попросите продавца прислать новую ссылку.',
    revokedHead: 'Ссылка на оплату отменена',
    revokedBody: 'Свяжитесь с продавцом, если это ошибка.',
    notFoundHead: 'Ссылка на оплату недействительна',
    notFoundBody: 'Проверьте ссылку или попросите продавца прислать её снова.',
    unavailableHead: 'Платежи сейчас недоступны',
    unavailableBody: 'Платёжный процессор не настроен. Приём платежей невозможен, списаний не было.',
    errorHead: 'Что-то пошло не так',
    loading: 'Загрузка…',
    language: 'Язык',
    poweredBy: 'Платежи обрабатывает Stripe. Данные карты вводятся напрямую в Stripe и не попадают на серверы WyoCSPay.',
    terms: 'Условия использования',
    privacy: 'Политика конфиденциальности',
    support: 'Поддержка',
    acceptPre: 'Я прочитал(а) и принимаю',
    acceptMid: 'и',
    acceptPost: '.',
    acceptRequired: 'Примите условия перед оплатой.',
    sellerNote: 'Вы платите указанному выше продавцу. WyoCSPay обрабатывает платёж и не является продавцом.',
    operatedBy: 'Страница оплаты управляется',
    rights: 'Все права защищены.',
    receiptNote: 'Сохраните ссылку выше на случай обращения в поддержку.',
    advertisement: 'Реклама',
    adNote: 'Платная реклама. Она не связана с вашим платежом и открывается в новом окне.',
    landingHead: 'Защищённая страница оплаты',
    landingBody: 'Здесь проходят платежи WyoCSPay. На этом адресе ничего вводить не нужно: платёж открывается по ссылке, которую присылает продавец.',
    landingAsk: 'Если вы ожидаете оплату, попросите продавца прислать ссылку на платёж ещё раз.',
    assuranceLabel: 'Безопасность и соответствие',
    assuranceCardsHead: 'Данные карты',
    assuranceCards: 'Форму карты на этой странице предоставляет Stripe. То, что вы в неё вводите, уходит в Stripe, а не к нам: WyoCSPay не получает и не хранит номер вашей карты.',
    assuranceStandardHead: 'Оценка',
    assuranceStandardNote: 'Стандарт, по которому оценена эта страница оплаты. Уровень SAQ A предназначен для страницы, которая оставляет всю обработку карт проверенному поставщику.',
    assuranceScanHead: 'Внешнее сканирование',
    assuranceScan: 'Системы, обслуживающие эту страницу, каждые три месяца проверяются на уязвимости поставщиком сканирования, одобренным PCI Security Standards Council.',
    assuranceScanLast: 'Последняя проверка пройдена',
    assuranceTlsHead: 'Соединение',
    assuranceTls: 'Страница передаётся по TLS 1.2 или 1.3, только с аутентифицированным шифрованием.',
    assuranceClose: 'Закрыть',
    stepMethod: 'Способ оплаты',
    stepConfirm: 'Подтверждение',
    stepResult: 'Результат',
    txnLabel: '№ транзакции',
    sessionLabel: 'Сессия истекает через',
    sessionExpired: 'Сессия истекла',
    sessionExpiredNote: 'Эта платёжная сессия истекла, и ссылка больше не действует. Пожалуйста, запросите новую ссылку на оплату.',
    payRequest: 'Запрос на оплату',
    invoiceNo: 'Номер счёта',
    orderSummary: 'Сводка заказа',
    sumSubtotal: 'Промежуточный итог',
    totalDue: 'Итого к оплате',
    continueBtn: 'Продолжить',
    changeMethod: 'Сменить способ',
    reviewLine: 'Проверьте и подтвердите оплату.',
    holdCard: 'Сумма авторизуется сразу; комиссия за обработку удерживается после того, как ваш банк одобрит списание.',
    resultBand: 'Платёж продавцу',
    payInfo: 'Информация о платеже',
    statusLabel: 'Статус',
    chargedLabel: 'Списанная сумма',
    chargedLabelNo: 'Запрошенная сумма',
    pendingHead: 'Обрабатываем ваш платёж',
    pendingSub: 'Пожалуйста, не закрывайте это окно - не обновляйте и не закрывайте его.',
    pendingKeep: 'Пожалуйста, не закрывайте это окно - не обновляйте и не закрывайте его.',
    stepSubmitted: 'Платёж отправлен',
    stepAuthorized: 'Авторизован вашим банком',
    stepConfirmed: 'Подтверждён WyoCSPay',
    okHint: 'Квитанция отправлена вам на почту. Вы можете спокойно вернуться к продавцу.',
    chooseAnother: 'Выбрать другой способ',
    securedTitle: 'Защищено WyoCSPay',
    securedBody: 'Обработано по зашифрованному соединению PCI DSS Level 1. Данные вашей карты введены напрямую в банке и никогда не попадали на эту страницу.',
    supportQ: 'Вопросы по этому платежу? Обратитесь в поддержку по адресу',
    timeoutHead: 'Мы всё ещё подтверждаем ваш платёж',
    timeoutSub: 'Это занимает больше времени, чем обычно. Ваш платёж не потерян - вы можете проверить снова ниже.',
    timeoutHint: 'От вашего банка пока нет подтверждения. Это может занять несколько минут. Проверьте снова сейчас, либо вы получите письмо, как только платёж пройдёт - платить повторно не нужно.',
    checkAgain: 'Проверить снова',
    trust3dsTitle: '3-D Secure 2.2',
    trust3dsBody: 'Каждое списание проходит аутентификацию напрямую в вашем банке-эмитенте.',
    trustPciTitle: 'PCI DSS Level 1',
    trustPciBody: 'Данные карты токенизируются и никогда не хранятся на наших серверах.',
    trust247Title: 'Круглосуточная поддержка',
    trust247Body: 'Наша команда отвечает на любой вопрос по транзакции в течение двух часов.',
    subPackageTitle: 'Ваш пакет WyoCSPay',
    account: 'Аккаунт',
    packageLbl: 'Пакет',
    billingLbl: 'Выставление счетов',
    billingRenewal: 'Ежемесячное продление',
    billingSetup: 'Первый месяц (настройка)',
    subAuthNote: 'Оплачивая, вы разрешаете ежемесячные списания за этот пакет до его отмены.',
    subPaidTo: 'Оплачено Wyoming Cloud Solutions LLC через Stripe.',
  },
  uk: {
    secure: 'Захищене з’єднання',
    merchant: 'Продавець',
    reference: 'Референс',
    description: 'Опис',
    amount: 'Сума',
    total: 'Разом до сплати',
    summary: 'Підсумок замовлення',
    payNow: 'Сплатити',
    paying: 'Надсилання…',
    chooseMethod: 'Оберіть спосіб оплати',
    methodsNote: 'Доступні способи залежать від вашого банку, країни та рахунку продавця.',
    unavailableTitle: 'Тут не пропонується',
    confirmingHead: 'Підтверджуємо ваш платіж',
    confirmingBody: 'Очікуємо підтвердження від платіжної мережі. Не закривайте й не оновлюйте сторінку.',
    confirmingWhy: 'Ваш банк повідомлено. Платіж вважається завершеним лише коли мережа підтвердить його безпосередньо нашим серверам.',
    okHead: 'Платіж підтверджено',
    okBody: 'отримав оплату.',
    failHead: 'Платіж відхилено',
    failBody: 'Ваш банк не авторизував списання. Кошти не списано.',
    failHint: 'Якщо це повториться, зверніться до банку або оберіть інший спосіб оплати.',
    tryAgain: 'Спробувати ще раз',
    amountCharged: 'Списана сума',
    amountAttempted: 'Запитана сума',
    paidAt: 'Підтверджено',
    method: 'Спосіб оплати',
    expiredHead: 'Термін дії посилання минув',
    expiredBody: 'Попросіть продавця надіслати нове посилання.',
    revokedHead: 'Посилання на оплату скасовано',
    revokedBody: 'Зв’яжіться з продавцем, якщо це помилка.',
    notFoundHead: 'Посилання на оплату недійсне',
    notFoundBody: 'Перевірте посилання або попросіть продавця надіслати його знову.',
    unavailableHead: 'Платежі зараз недоступні',
    unavailableBody: 'Платіжний процесор не налаштовано. Приймання платежів неможливе, списань не було.',
    errorHead: 'Щось пішло не так',
    loading: 'Завантаження…',
    language: 'Мова',
    poweredBy: 'Платежі обробляє Stripe. Дані картки вводяться безпосередньо у Stripe і не потрапляють на сервери WyoCSPay.',
    terms: 'Умови користування',
    privacy: 'Політика конфіденційності',
    support: 'Підтримка',
    acceptPre: 'Я прочитав(ла) і приймаю',
    acceptMid: 'і',
    acceptPost: '.',
    acceptRequired: 'Прийміть умови перед оплатою.',
    sellerNote: 'Ви платите зазначеному вище продавцю. WyoCSPay обробляє платіж і не є продавцем.',
    operatedBy: 'Сторінкою оплати керує',
    rights: 'Усі права захищено.',
    receiptNote: 'Збережіть референс вище на випадок звернення до підтримки.',
    advertisement: 'Реклама',
    adNote: 'Платна реклама. Вона не стосується вашого платежу і відкривається в новому вікні.',
    landingHead: 'Захищена сторінка оплати',
    landingBody: 'Тут здійснюються платежі WyoCSPay. На цій адресі нічого вводити не потрібно: платіж відкривається за посиланням, яке надсилає продавець.',
    landingAsk: 'Якщо ви очікуєте оплату, попросіть продавця надіслати посилання на платіж ще раз.',
    assuranceLabel: 'Безпека та відповідність',
    assuranceCardsHead: 'Дані картки',
    assuranceCards: 'Форму картки на цій сторінці надає Stripe. Те, що ви в неї вводите, іде до Stripe, а не до нас: WyoCSPay не отримує і не зберігає номер вашої картки.',
    assuranceStandardHead: 'Оцінка',
    assuranceStandardNote: 'Стандарт, за яким оцінено цю сторінку оплати. Рівень SAQ A призначений для сторінки, яка залишає всю обробку карток перевіреному постачальнику.',
    assuranceScanHead: 'Зовнішнє сканування',
    assuranceScan: 'Системи, які обслуговують цю сторінку, кожні три місяці перевіряються на вразливості постачальником сканування, схваленим PCI Security Standards Council.',
    assuranceScanLast: 'Останню перевірку пройдено',
    assuranceTlsHead: 'З’єднання',
    assuranceTls: 'Сторінка передається через TLS 1.2 або 1.3, лише з автентифікованим шифруванням.',
    assuranceClose: 'Закрити',
    stepMethod: 'Спосіб оплати',
    stepConfirm: 'Підтвердження',
    stepResult: 'Результат',
    txnLabel: '№ транзакції',
    sessionLabel: 'Сесія завершується за',
    sessionExpired: 'Сесія завершилася',
    sessionExpiredNote: 'Ця платіжна сесія завершилася, і посилання більше не діє. Будь ласка, запросіть нове посилання на оплату.',
    payRequest: 'Запит на оплату',
    invoiceNo: 'Номер рахунка',
    orderSummary: 'Підсумок замовлення',
    sumSubtotal: 'Проміжний підсумок',
    totalDue: 'Разом до сплати',
    continueBtn: 'Продовжити',
    changeMethod: 'Змінити спосіб',
    reviewLine: 'Перевірте та підтвердьте оплату.',
    holdCard: 'Сума авторизується одразу; комісія за обробку стягується після того, як ваш банк схвалить списання.',
    resultBand: 'Платіж продавцю',
    payInfo: 'Інформація про платіж',
    statusLabel: 'Статус',
    chargedLabel: 'Списана сума',
    chargedLabelNo: 'Запитана сума',
    pendingHead: 'Обробляємо ваш платіж',
    pendingSub: 'Будь ласка, не закривайте це вікно - не оновлюйте й не закривайте його.',
    pendingKeep: 'Будь ласка, не закривайте це вікно - не оновлюйте й не закривайте його.',
    stepSubmitted: 'Платіж надіслано',
    stepAuthorized: 'Авторизовано вашим банком',
    stepConfirmed: 'Підтверджено WyoCSPay',
    okHint: 'Квитанцію надіслано вам на пошту. Ви можете безпечно повернутися до продавця.',
    chooseAnother: 'Обрати інший спосіб',
    securedTitle: 'Захищено WyoCSPay',
    securedBody: 'Оброблено через зашифроване з’єднання PCI DSS Level 1. Дані вашої картки введено безпосередньо в банку і вони ніколи не торкалися цієї сторінки.',
    supportQ: 'Питання щодо цього платежу? Зверніться до підтримки за адресою',
    timeoutHead: 'Ми ще підтверджуємо ваш платіж',
    timeoutSub: 'Це триває довше, ніж зазвичай. Ваш платіж не втрачено - ви можете перевірити ще раз нижче.',
    timeoutHint: 'Від вашого банку ще немає підтвердження. Це може зайняти кілька хвилин. Перевірте ще раз зараз, або ви отримаєте лист, щойно платіж пройде - платити повторно не потрібно.',
    checkAgain: 'Перевірити ще раз',
    trust3dsTitle: '3-D Secure 2.2',
    trust3dsBody: 'Кожне списання автентифікується безпосередньо у вашому банку-емітенті.',
    trustPciTitle: 'PCI DSS Level 1',
    trustPciBody: 'Дані картки токенізуються і ніколи не зберігаються на наших серверах.',
    trust247Title: 'Цілодобова підтримка',
    trust247Body: 'Наша команда відповідає на кожен запит щодо транзакції протягом двох годин.',
    subPackageTitle: 'Ваш пакет WyoCSPay',
    account: 'Обліковий запис',
    packageLbl: 'Пакет',
    billingLbl: 'Виставлення рахунків',
    billingRenewal: 'Щомісячне продовження',
    billingSetup: 'Перший місяць (налаштування)',
    subAuthNote: 'Сплачуючи, ви дозволяєте щомісячні списання за цей пакет до його скасування.',
    subPaidTo: 'Сплачено Wyoming Cloud Solutions LLC через Stripe.',
  },
} as const;

export type Lang = keyof typeof STRINGS;

/**
 * Widened to `string` per key: `as const` gives each language its own literal
 * types, which would make the translations mutually incompatible.
 */
export type Strings = Record<keyof (typeof STRINGS)['en'], string>;

export interface LanguageOption {
  code: Lang;
  /** Endonym - a speaker recognises their own language faster than a translation of its name. */
  label: string;
  short: string;
  locale: string;
}

export const LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English', short: 'EN', locale: 'en-US' },
  { code: 'bg', label: 'Български', short: 'BG', locale: 'bg-BG' },
  { code: 'de', label: 'Deutsch', short: 'DE', locale: 'de-DE' },
  { code: 'fr', label: 'Français', short: 'FR', locale: 'fr-FR' },
  { code: 'es', label: 'Español', short: 'ES', locale: 'es-ES' },
  { code: 'it', label: 'Italiano', short: 'IT', locale: 'it-IT' },
  { code: 'ru', label: 'Русский', short: 'RU', locale: 'ru-RU' },
  { code: 'uk', label: 'Українська', short: 'UK', locale: 'uk-UA' },
];

export const LOCALES: Record<Lang, string> = {
  en: 'en-US',
  bg: 'bg-BG',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
  ru: 'ru-RU',
  uk: 'uk-UA',
};

/** Match the browser's preference to a language we actually have. */
export function detectLanguage(): Lang {
  for (const pref of navigator.languages ?? [navigator.language]) {
    const base = (pref ?? '').toLowerCase().split('-')[0];
    const hit = LANGUAGES.find((l) => l.code === base);
    if (hit) return hit.code;
  }
  return 'en';
}

/**
 * Map our language to a locale the Payment Element supports.
 *
 * Stripe does not offer Ukrainian. Falling back to English rather than Russian
 * is deliberate - substituting one for the other would be its own insult.
 */
export function stripeLocale(lang: Lang): 'en' | 'bg' | 'de' | 'fr' | 'es' | 'it' | 'ru' {
  return lang === 'uk' ? 'en' : lang;
}
